import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Tabs } from "antd";

import { PortfolioPageShell } from "@/views/Portfolio/shared";
import { MetadataDashboardTemplate } from "@/components/Templates/MetadataDashboardTemplate";
import StatCard from "@/components/UI/Cards/StatCard";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useUser } from "@/contexts/UserContext";
import { apiFetch, apiFetchJson } from "@/utils/apiFetch";
import DiffModal from "@/components/DataMastery/DiffModal";
import type {
  QueueItemDetail,
  Comment,
  AuditEntry,
  MasteryAction,
} from "@/components/DataMastery/DiffModal";
import DataMasteryGaps from "./DataMasteryGaps";

// ── API helpers ───────────────────────────────────────────────────────────────

export interface MasteryQueueCounts {
  needs_review: number;
  /**
   * Every item in the table, open or closed — what the "All" tab counts.
   * Optional: a backend older than this field's introduction omits it, and the
   * frontend deploys independently, so the tab must not render "undefined"
   * during the skew (or after a backend rollback).
   */
  total?: number;
  /** Items a steward has already resolved/rejected/dismissed. Same caveat. */
  closed?: number;
  by_type: {
    conflict: number;
    pair_review: number;
    pi_review: number;
  };
  high_priority: number;
  overdue: number;
}

async function fetchMasteryCounts(): Promise<MasteryQueueCounts> {
  return apiFetchJson<MasteryQueueCounts>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/counts/`,
  );
}

async function fetchMasteryItemDetail(
  itemId: string,
): Promise<QueueItemDetail> {
  return apiFetchJson<QueueItemDetail>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/item/${encodeURIComponent(itemId)}/`,
  );
}

async function patchMasteryItem(
  itemId: string,
  patch: {
    priority?: string | null;
    status?: string | null;
    action?: string;
    notes?: string;
  },
): Promise<void> {
  await apiFetch(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/${encodeURIComponent(itemId)}/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
}

async function fetchAuditHistory(itemId: string): Promise<AuditEntry[]> {
  return apiFetchJson<AuditEntry[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/${encodeURIComponent(itemId)}/history/`,
  );
}

// ── Type tabs ─────────────────────────────────────────────────────────────────

type QueueTypeTab =
  | "all"
  | "conflict"
  | "pair_review"
  | "pi_review"
  | "data_gaps";

// Pre-mastering is the auto-admitted golden catalog, not a human review
// backlog — it is intentionally excluded from the review queue (the backend
// also drops it from the counts and table rows). Golden records remain
// visible on the site/investigator profile pages.
const TYPE_TAB_LABELS: Record<
  Exclude<QueueTypeTab, "all" | "data_gaps">,
  string
> = {
  conflict: "Conflicts",
  pair_review: "Pair Reviews",
  pi_review: "PI Dedup",
};

// Data gaps are an enrichment backlog, not adjudication debt — they get their
// own tab and are excluded from the needs-review counts entirely.
async function fetchGapsCount(): Promise<number> {
  const res = await apiFetchJson<{ count: number }>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-gaps/?limit=1`,
  );
  return res.count;
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function DataMasteryQueue() {
  const navigate = useNavigate();
  const { scope } = useScopeParams();
  const { id } = useParams<{ id: string }>();
  const { userInfo } = useUser();
  const userFullName = userInfo
    ? `${userInfo.first_name} ${userInfo.last_name}`.trim()
    : "";
  const queryClient = useQueryClient();

  // Task-focused counts — same source as the table (mastery_queue_view)
  const { data: countsData } = useQuery({
    queryKey: ["mastery-queue-counts", scope],
    queryFn: fetchMasteryCounts,
    enabled: !!scope,
    staleTime: 60_000,
  });
  const counts: MasteryQueueCounts = countsData ?? {
    needs_review: 0,
    total: 0,
    closed: 0,
    by_type: { conflict: 0, pair_review: 0, pi_review: 0 },
    high_priority: 0,
    overdue: 0,
  };

  const [typeTab, setTypeTab] = useState<QueueTypeTab>("all");
  const presetFilters = useMemo(
    () =>
      typeTab === "all" || typeTab === "data_gaps"
        ? undefined
        : { item_source: [typeTab] },
    [typeTab],
  );

  const { data: gapsCount } = useQuery({
    queryKey: ["mastery-gaps-count", scope],
    queryFn: fetchGapsCount,
    enabled: !!scope,
    staleTime: 60_000,
  });

  // Modal state — the row supplies the id, the detail endpoint supplies
  // everything else (no unbounded list fetch). Local state (not URL) so the
  // workspace unsaved-changes navigation guard doesn't intercept open/close.
  // Opened either by a row click or by ?item=<id> — the Decision Log links
  // here so a steward can reach a past decision and Reopen it. Kept in local
  // state (not the URL) once open, so the workspace unsaved-changes guard
  // doesn't intercept open/close; the param is consumed once on mount.
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalItemId, setModalItemId] = useState<string | null>(() =>
    searchParams.get("item"),
  );

  // Drop the param so a refresh or a later close doesn't reopen the item.
  useEffect(() => {
    if (!searchParams.get("item")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("item");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: itemDetail, isLoading: itemDetailLoading } = useQuery({
    queryKey: ["mastery-item-detail", scope, modalItemId],
    queryFn: () => fetchMasteryItemDetail(modalItemId!),
    enabled: !!scope && !!modalItemId,
    staleTime: 30_000,
  });

  // Local optimistic overrides for priority/status while modal is open
  const [localOverrides, setLocalOverrides] = useState<
    Partial<QueueItemDetail>
  >({});
  const modalItem = itemDetail ? { ...itemDetail, ...localOverrides } : null;

  const [draft, setDraft] = useState("");

  const closeModal = useCallback(() => {
    setModalItemId(null);
    setDraft("");
    setLocalOverrides({});
  }, []);

  const openModal = useCallback((itemId: string) => {
    setModalItemId(itemId);
    setDraft("");
    setLocalOverrides({});
  }, []);

  const { data: auditHistoryData, isLoading: auditHistoryLoading } = useQuery({
    queryKey: ["mastery-audit", scope, modalItemId],
    queryFn: () => fetchAuditHistory(modalItemId!),
    enabled: !!scope && !!modalItemId,
    staleTime: 30_000,
  });
  const auditHistory = useMemo(
    () => auditHistoryData ?? [],
    [auditHistoryData],
  );

  // Notes are persisted through the PATCH `notes` field; render the note_added
  // audit entries as the Notes thread.
  const comments: Comment[] = useMemo(
    () =>
      auditHistory
        .filter((e) => e.action === "note_added" && e.notes)
        .map((e, i) => ({
          id: `n-${i}-${e.performed_at}`,
          author: e.performed_by ?? "Unknown",
          timestamp: e.performed_at,
          text: e.notes,
        }))
        .reverse(),
    [auditHistory],
  );

  const invalidateQueue = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["mastery-queue-counts", scope],
    });
    queryClient.invalidateQueries({
      queryKey: ["mastery-item-detail", scope, modalItemId],
    });
    queryClient.invalidateQueries({
      queryKey: ["mastery-audit", scope, modalItemId],
    });
  }, [queryClient, scope, modalItemId]);

  const statusMutation = useMutation({
    mutationFn: (patch: {
      priority?: string | null;
      status?: string | null;
      action?: string;
      notes?: string;
    }) => patchMasteryItem(modalItemId!, patch),
    onSuccess: invalidateQueue,
  });

  const handlePriorityChange = (val: string | null) => {
    setLocalOverrides((prev) => ({
      ...prev,
      priority: val as QueueItemDetail["priority"],
    }));
    statusMutation.mutate({ priority: val ?? null });
  };

  const handleAction = (
    action: MasteryAction,
    opts?: { keepOpen?: boolean },
  ) => {
    if (!modalItemId) return;
    statusMutation.mutate({ action });
    if (opts?.keepOpen) {
      setLocalOverrides({});
    } else {
      closeModal();
    }
  };

  const handleSubmitComment = () => {
    const text = draft.trim();
    if (!text || !modalItemId) return;
    statusMutation.mutate({ notes: text });
    setDraft("");
  };

  const constants = { wikiLink: "", idLink: undefined };

  const handleRowClick = useCallback(
    (record: Record<string, unknown>) => ({
      style: { cursor: "pointer" as const },
      onClick: (e: React.MouseEvent) => {
        if (
          (e.target as HTMLElement).closest(".ant-select, .ant-select-selector")
        )
          return;
        const itemId = record.item_id as string | undefined;
        if (itemId) openModal(itemId);
      },
    }),
    [openModal],
  );

  const queueTab = (
    <>
      {/* Summary cards — reuses StatCard from components/UI/Cards/StatCard */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Needs Review"
          value={counts.needs_review}
          description="Open items awaiting a decision"
        />
        <StatCard
          label="High Priority"
          value={counts.high_priority}
          description="Open items marked high priority"
        />
        <StatCard
          label="Overdue"
          value={counts.overdue}
          description="Past their review due date"
        />
      </div>

      {/* Type tabs — scope the table to one queue type. Data Gaps swaps in its
          own table (enrichment backlog; not part of the review counts). */}
      <Tabs
        activeKey={typeTab}
        onChange={(k) => setTypeTab(k as QueueTypeTab)}
        size="small"
        items={[
          { key: "all", label: `All (${counts.total ?? counts.needs_review})` },
          ...(
            Object.entries(TYPE_TAB_LABELS) as Array<
              [Exclude<QueueTypeTab, "all" | "data_gaps">, string]
            >
          ).map(([key, label]) => ({
            key,
            label: `${label} (${counts.by_type[key]})`,
          })),
          {
            key: "data_gaps",
            label: `Data Gaps${gapsCount != null ? ` (${gapsCount.toLocaleString()})` : ""}`,
          },
        ]}
      />

      {typeTab === "data_gaps" ? (
        <DataMasteryGaps />
      ) : (
        /* Standard Vivo advanced table */
        <MetadataDashboardTemplate
          key={`portfolio-mastery-${scope}-${id}-${typeTab}`}
          type="portfolio-mastery"
          constants={constants}
          selectionIdentifierColumn="entity_display_name"
          presetFilters={presetFilters}
          onRow={handleRowClick}
        />
      )}
    </>
  );

  return (
    <PortfolioPageShell
      title="Review Queue"
      tooltipText="Triage and resolve entity identity conflicts across sites and investigators."
      headerExtra={
        <Button onClick={() => navigate("/portfolio/data/mastery")}>
          Mastery Analytics
        </Button>
      }
    >
      {queueTab}

      {/* Record detail modal */}
      {modalItemId && (
        <DiffModal
          item={modalItem}
          loading={itemDetailLoading}
          comments={comments}
          auditHistory={auditHistory}
          auditHistoryLoading={auditHistoryLoading}
          onClose={closeModal}
          onOpenItem={openModal}
          draft={draft}
          onDraftChange={setDraft}
          onSubmitComment={handleSubmitComment}
          onPriorityChange={handlePriorityChange}
          onAction={handleAction}
          userFullName={userFullName}
        />
      )}
    </PortfolioPageShell>
  );
}
